import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { getCreditPackages, createCreditRequest } from '../../services/creditService';
import { CreditPackage } from '../../types';

interface CreditRequestDialogProps {
  onClose: () => void;
  showToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

const CreditRequestDialog: React.FC<CreditRequestDialogProps> = ({ onClose, showToast }) => {
  const { selectedOrganization } = useApp();
  const { currentUser, userData } = useAuth();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getCreditPackages()
      .then((pkgs) => {
        setPackages(pkgs);
        if (pkgs.length > 0) setSelectedPackage(pkgs[0]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (!selectedPackage || !selectedOrganization?.id || !currentUser) return;

    setSubmitting(true);
    try {
      await createCreditRequest(
        selectedOrganization.id,
        selectedOrganization.name,
        selectedPackage,
        {
          userId: currentUser.uid,
          name: userData?.name || currentUser.displayName || 'Unknown'
        }
      );

      showToast?.('Credit request submitted. A platform administrator will review it shortly.', 'success');
      onClose();
    } catch (error) {
      console.error('Error submitting credit request:', error);
      showToast?.('Failed to submit credit request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
        <h3 className="text-lg font-semibold text-secondary-900 mb-2">Request Message Credits</h3>
        <p className="text-sm text-secondary-500 mb-6">
          Select a credit package to request. Your request will be reviewed by the platform administrator.
        </p>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : packages.length === 0 ? (
          <div className="py-8 text-center text-secondary-500 text-sm">
            No credit packages available. Contact your platform administrator.
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {packages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg)}
                className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  selectedPackage?.id === pkg.id
                    ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                    : 'border-secondary-200 hover:border-secondary-300'
                }`}
              >
                <div className="text-left">
                  <p className="font-semibold text-secondary-900">{pkg.name}</p>
                  <p className="text-sm text-secondary-500">
                    {pkg.creditAmount.toLocaleString()} credits
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-secondary-900">
                    {pkg.currency} {pkg.price.toLocaleString()}
                  </p>
                  <p className="text-xs text-secondary-400">
                    {(pkg.price / pkg.creditAmount).toFixed(2)} per credit
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !selectedPackage || packages.length === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreditRequestDialog;
